import { useCallback, useSyncExternalStore } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from './useCurrentUser';
import { OWNER_PUBKEY } from '@/lib/owner';
import { getStoredLicense, setStoredLicense } from '@/lib/license';

export type Tier = 'free' | 'pro';
export type BillingPlan = 'monthly' | 'yearly';

/**
 * Subscription architecture designed so pricing/tiers can change later.
 * Pricing is centralized here. Yearly is billed as one lump payment at a
 * discount versus 12× the monthly price.
 */
export const PRICING = {
  currency: 'USD',
  proMonthlyUsd: 7,
  /** One lump yearly payment (≈ 2 months free vs 12× monthly). */
  proYearlyUsd: 70,
} as const;

/** Number of days each plan grants. */
export const PLAN_DAYS: Record<BillingPlan, number> = {
  monthly: 30,
  yearly: 365,
};

/** USD price for a given plan. */
export function planPriceUsd(plan: BillingPlan): number {
  return plan === 'yearly' ? PRICING.proYearlyUsd : PRICING.proMonthlyUsd;
}

/** Percentage saved by choosing yearly over 12× monthly. */
export const YEARLY_SAVINGS_PCT = Math.round(
  (1 - PRICING.proYearlyUsd / (PRICING.proMonthlyUsd * 12)) * 100,
);

/** Shape returned by the server entitlement endpoint. */
interface Entitlement {
  pro: boolean;
  expiresAt: number | null;
  plan: string | null;
}

const FREE: Entitlement = { pro: false, expiresAt: null, plan: null };

/** Subscribe to local license-key changes so the hook re-resolves live. */
function useStoredLicense(): string | null {
  return useSyncExternalStore(
    (onChange) => {
      window.addEventListener('bchradar:license-changed', onChange);
      window.addEventListener('storage', onChange);
      return () => {
        window.removeEventListener('bchradar:license-changed', onChange);
        window.removeEventListener('storage', onChange);
      };
    },
    () => getStoredLicense(),
    () => null,
  );
}

async function fetchEntitlementByPubkey(
  pubkey: string,
  signal?: AbortSignal,
): Promise<Entitlement> {
  // Owner is always Pro — resolve instantly without waiting on the network.
  if (pubkey === OWNER_PUBKEY) {
    return { pro: true, expiresAt: null, plan: 'owner' };
  }
  const res = await fetch(`/.netlify/functions/entitlement?pubkey=${pubkey}`, {
    signal,
  });
  if (!res.ok) return FREE;
  const data = (await res.json()) as Partial<Entitlement>;
  return normalize(data);
}

async function fetchEntitlementByLicense(
  license: string,
  signal?: AbortSignal,
): Promise<Entitlement> {
  const res = await fetch(
    `/.netlify/functions/entitlement?license=${encodeURIComponent(license)}`,
    { signal },
  );
  if (!res.ok) return FREE;
  const data = (await res.json()) as Partial<Entitlement>;
  return normalize(data);
}

function normalize(data: Partial<Entitlement>): Entitlement {
  return {
    pro: !!data.pro,
    expiresAt: typeof data.expiresAt === 'number' ? data.expiresAt : null,
    plan: typeof data.plan === 'string' ? data.plan : null,
  };
}

/**
 * Pro entitlement is SERVER-AUTHORITATIVE. It can be tied to either:
 *   • a locally-stored LICENSE KEY minted after a BCH payment (no login), or
 *   • the logged-in Nostr pubkey (owner override + optional Nostr-tied Pro).
 * The client cannot forge it: `isPro` reflects what the server returns. A
 * payment is claimed via `claimPayment`, which the server only honors after
 * re-verifying the BCH transaction on-chain.
 */
export function useSubscription() {
  const { user } = useCurrentUser();
  const pubkey = user?.pubkey;
  const license = useStoredLicense();
  const queryClient = useQueryClient();

  // Entitlement tied to the logged-in Nostr account (if any).
  const nostrQuery = useQuery({
    queryKey: ['entitlement', 'pubkey', pubkey],
    enabled: !!pubkey,
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    queryFn: ({ signal }) => fetchEntitlementByPubkey(pubkey as string, signal),
  });

  // Entitlement tied to a locally-stored license key (the no-login path).
  const licenseQuery = useQuery({
    queryKey: ['entitlement', 'license', license],
    enabled: !!license,
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    queryFn: ({ signal }) =>
      fetchEntitlementByLicense(license as string, signal),
  });

  const isOwner = pubkey === OWNER_PUBKEY;

  const now = Date.now();
  const active = (e: Entitlement | undefined): boolean =>
    !!e && e.pro && (e.expiresAt === null || e.expiresAt > now);

  const nostrEnt = nostrQuery.data;
  const licenseEnt = licenseQuery.data;

  // Pick whichever active entitlement grants Pro. Owner/Nostr takes precedence
  // when active; otherwise fall back to the license.
  const state: Entitlement = active(nostrEnt)
    ? (nostrEnt as Entitlement)
    : active(licenseEnt)
      ? (licenseEnt as Entitlement)
      : FREE;

  const isPro = isOwner || active(state);

  /**
   * Claim a completed BCH payment. The server re-verifies the transaction
   * on-chain before granting Pro. By default (no Nostr login) the server MINTS
   * a license key which we store locally so Pro persists on this device. If a
   * Nostr user is logged in, Pro is granted to their pubkey instead. Returns
   * the license key when one was minted. Throws on failure.
   */
  const claimPayment = useCallback(
    async (opts: {
      amount: string;
      since: number;
      plan: BillingPlan;
    }): Promise<{ license?: string }> => {
      const body: Record<string, string | number> = {
        amount: opts.amount,
        since: opts.since,
        plan: opts.plan,
      };
      // Prefer a Nostr-tied grant only for the owner (so their account stays
      // Pro). Everyone else uses the frictionless license path.
      if (pubkey === OWNER_PUBKEY) {
        body.pubkey = pubkey;
      } else {
        const existing = getStoredLicense();
        if (existing) body.license = existing;
      }

      const res = await fetch('/.netlify/functions/entitlement', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = (await res.json()) as {
        ok?: boolean;
        error?: string;
        license?: string;
      };
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'entitlement-failed');
      }
      // Persist a minted/echoed license key so Pro sticks on this device.
      if (data.license) {
        setStoredLicense(data.license);
      }
      await queryClient.invalidateQueries({ queryKey: ['entitlement'] });
      return { license: data.license };
    },
    [pubkey, queryClient],
  );

  const isLoading =
    (!!pubkey && nostrQuery.isLoading) ||
    (!!license && licenseQuery.isLoading);

  return {
    tier: (isPro ? 'pro' : 'free') as Tier,
    expiresAt: isOwner ? null : state.expiresAt,
    method: isPro && !isOwner ? ('bch' as const) : null,
    plan: (state.plan === 'yearly' || state.plan === 'monthly'
      ? state.plan
      : null) as BillingPlan | null,
    isPro,
    isOwner,
    isLoading,
    /** Whether a Nostr user is logged in (owner uses this). */
    loggedIn: !!pubkey,
    /** Whether Pro is currently held via a local license key. */
    hasLicense: active(licenseEnt),
    claimPayment,
  };
}
